/**
 * API client pour les rapports e-commerce
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function getTenantId(): string | null {
  try {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      const tenant = JSON.parse(tenantData);
      return tenant?.id || null;
    }
  } catch (e) {
    console.error('Failed to parse tenant data:', e);
  }
  return null;
}

function getAuthToken(): string | null {
  const customerToken = localStorage.getItem('customer_token');
  const staffToken = localStorage.getItem('auth_token');
  return customerToken || staffToken;
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const tenantId = getTenantId();
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
}

export interface EcomReportPreview {
  period_start: string;
  period_end: string;
  total_orders_count: number;
  completed_orders_count: number;
  cancelled_orders_count: number;
  pending_orders_count: number;
  total_ca_ttc_cents: number;
  total_ca_ht_cents: number;
  total_tax_cents: number;
  vat_breakdown: {
    rate_0: { base_cents: number };
    rate_6: { base_cents: number; amount_cents: number };
    rate_21: { base_cents: number; amount_cents: number };
  };
  payment_methods: {
    wallet_cents: number;
    card_cents: number;
    cash_cents: number;
  };
  cancellations: { count: number; total_cents: number };
  refunds: {
    count: number;
    total_cents: number;
    by_type: Array<{ refund_type: string; count: number; total_cents: number }>;
  };
  delivery_fees_cents: number;
  delivery_breakdown: { delivery_count: number; pickup_count: number };
  discount_total_cents: number;
  average_basket_cents: number;
  orders_by_status: Array<{ status: string; count: number; total_cents: number }>;
  enfact_invoices: {
    count: number;
    total_ht_cents: number;
    total_ttc_cents: number;
    invoices: Array<{
      id: string;
      enfact_invoice_number: string;
      customer_name: string | null;
      total_ht_cents: number;
      total_ttc_cents: number;
      status: string;
    }>;
  };
  existing_report?: EcomReport;
}

export interface EcomReport {
  id: string;
  tenant_id: string;
  report_number: string;
  sequence: number;
  period_start: string;
  period_end: string;
  total_orders_count: number;
  completed_orders_count: number;
  total_ca_ttc_cents: number;
  total_ca_ht_cents: number;
  total_tax_cents: number;
  vat_0_base_cents: number;
  vat_6_base_cents: number;
  vat_6_amount_cents: number;
  vat_21_base_cents: number;
  vat_21_amount_cents: number;
  payment_wallet_cents: number;
  payment_card_cents: number;
  payment_cash_cents: number;
  cancellations_count: number;
  cancellations_cents: number;
  refunds_count: number;
  refunds_cents: number;
  delivery_fees_cents: number;
  discount_total_cents: number;
  delivery_count: number;
  pickup_count: number;
  average_basket_cents: number;
  generated_by: string | null;
  generated_by_name: string | null;
  pdf_url: string | null;
  pdf_hash: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const ecomReportApi = {
  async preview(from: string, to: string): Promise<EcomReportPreview> {
    const params = new URLSearchParams({ from, to });
    const response = await fetch(`${API_BASE_URL}/ecom-reports/preview?${params}`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la prévisualisation');
    }
    return response.json();
  },

  async create(dto: {
    from: string;
    to: string;
    notes?: string;
    generated_by?: string;
    generated_by_name?: string;
  }): Promise<EcomReport> {
    const response = await fetch(`${API_BASE_URL}/ecom-reports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la création du rapport');
    }
    return response.json();
  },

  async list(limit = 30, offset = 0): Promise<{ reports: EcomReport[]; total: number }> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    const response = await fetch(`${API_BASE_URL}/ecom-reports?${params}`, {
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error('Erreur lors de la récupération des rapports');
    return response.json();
  },

  async get(id: string): Promise<EcomReport> {
    const response = await fetch(`${API_BASE_URL}/ecom-reports/${id}`, {
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error('Erreur lors de la récupération du rapport');
    return response.json();
  },

  getExportCsvUrl(from: string, to: string): string {
    return `${API_BASE_URL}/ecom-reports/export-csv?from=${from}&to=${to}`;
  },

  getExportLinesCsvUrl(from: string, to: string): string {
    return `${API_BASE_URL}/ecom-reports/export-lines-csv?from=${from}&to=${to}`;
  },
};
